import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Check, ChevronDown } from 'lucide-react';
import { apiClient } from '../../lib/api';

export interface PickedProduct {
  _id: string;
  displayName: string;
  basePrice: number;
  drugName?: string;
  concentration?: string;
  format?: string;
  size?: string;
}

/**
 * Searchable product picker.
 *
 * Replaces a native <select> populated by one bulk fetch. That approach had two
 * problems: the products endpoint caps `limit` at 100, so any catalog larger
 * than that was silently truncated and the missing products were simply
 * unreachable; and several hundred concatenated <option> strings is unusable
 * even when they do all load.
 *
 * Search runs server-side (the endpoint already searches drugName, displayName,
 * categories and description), so every product is reachable regardless of
 * catalog size, and the result count makes truncation visible instead of silent.
 */
interface Props {
  value: string;
  onChange: (product: PickedProduct | null) => void;
  /** Product ids already chosen elsewhere — shown greyed with a note. */
  excludeIds?: string[];
  placeholder?: string;
  disabled?: boolean;
}

const PAGE_SIZE = 25;

const money = (n?: number) =>
  typeof n === 'number'
    ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

export const ProductPicker: React.FC<Props> = ({
  value,
  onChange,
  excludeIds = [],
  placeholder = 'Search products by name, drug, or category…',
  disabled = false
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PickedProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<PickedProduct | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const seq = useRef(0);

  // Resolve an id supplied from outside (e.g. an edit form) to a label.
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?._id === value) return;
    (async () => {
      const res = await apiClient.getProductsByIds([value]);
      const p = (res as any)?.data?.products?.[0];
      if (p) setSelected(p);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const search = useCallback(async (q: string) => {
    const mine = ++seq.current;
    setLoading(true);
    const res = await apiClient.getProducts({ limit: PAGE_SIZE, search: q.trim() || undefined });
    // Ignore a slow response that lost the race to a newer keystroke.
    if (mine !== seq.current) return;
    if (res.success) {
      setResults(((res as any).data?.products || []) as PickedProduct[]);
      setTotal((res as any).data?.pagination?.total ?? 0);
    }
    setActiveIndex(0);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => { void search(query); }, 200);
    return () => clearTimeout(t);
  }, [query, open, search]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const choose = (p: PickedProduct) => {
    setSelected(p);
    onChange(p);
    setOpen(false);
    setQuery('');
  };

  const clear = () => {
    setSelected(null);
    onChange(null);
    setQuery('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const p = results[activeIndex];
      if (p && !excludeIds.includes(p._id)) choose(p);
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={rootRef} className="relative flex-1 min-w-0">
      {selected && !open ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => { setOpen(true); setQuery(''); }}
          className="w-full text-left border border-gray-300 rounded-md px-3 py-2 text-sm bg-white hover:border-gray-400 disabled:bg-gray-50"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-gray-900 truncate">{selected.displayName}</div>
              <div className="text-xs text-gray-500 truncate">
                {[selected.drugName, selected.concentration, selected.format].filter(Boolean).join(' • ') || ' '}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-gray-500">cost {money(selected.basePrice)}</span>
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </button>
      ) : (
        <div className="relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            autoFocus={open}
            disabled={disabled}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="w-full border border-gray-300 rounded-md pl-9 pr-8 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
          />
          {(query || selected) && (
            <button
              type="button"
              onClick={clear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              aria-label="Clear product"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-y-auto">
          {loading && <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>}

          {!loading && results.length === 0 && (
            <div className="px-3 py-3 text-sm text-gray-500">
              No products match “{query}”.
            </div>
          )}

          {!loading && results.map((p, i) => {
            const taken = excludeIds.includes(p._id);
            const isSelected = p._id === value;
            return (
              <button
                key={p._id}
                type="button"
                disabled={taken}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => choose(p)}
                className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 ${
                  taken ? 'opacity-40 cursor-not-allowed' : i === activeIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {p.displayName}
                      {taken && <span className="ml-2 text-xs text-gray-500">already added</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {[p.drugName, p.concentration, p.format, p.size].filter(Boolean).join(' • ')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-gray-500">cost {money(p.basePrice)}</span>
                    {isSelected && <Check className="h-4 w-4 text-blue-600" />}
                  </div>
                </div>
              </button>
            );
          })}

          {/* Make truncation visible. The old select silently dropped everything
              past the endpoint's 100-row cap. */}
          {!loading && total > results.length && (
            <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
              Showing {results.length} of {total} — keep typing to narrow the list.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductPicker;
